/**
 * The one line a tool call gets when it is collapsed.
 *
 * A run of tool calls is mostly noise until you want one of them, so the
 * collapsed row has to carry the single thing that distinguishes this call from
 * its neighbours: the command that ran, the file that was edited, the pattern
 * that was searched. The tool NAME alone does not — six rows reading "Bash" tell
 * you nothing that "6 tool calls" did not.
 *
 * The rule is deliberately generic rather than a per-tool table: tools arrive
 * from MCP servers and from apps this file has never heard of, and a table would
 * silently degrade to the bare name for every one of them.
 */

import { argString } from './toolArgs'

/**
 * Argument names that identify a call, most-identifying first. `command` beats
 * `description` because two Bash calls differ by their command, not by prose.
 */
const IDENTIFYING_ARGS = [
  'command',
  'file_path',
  'filePath',
  'path',
  'pattern',
  'query',
  'url',
  'description',
  'title',
  'name',
  'prompt',
  'text',
]

/** The first line of a string, trimmed — a summary row is one line tall. */
function firstLine(value: string): string {
  const line = value.split('\n', 1)[0] ?? ''
  return line.trim()
}

/**
 * A short, human-meaningful description of what this call is doing, or '' when
 * the arguments carry nothing worth putting on one line (the caller then shows
 * the tool name alone).
 */
export function summarizeToolArgs(args: unknown): string {
  // Every read goes through `argString`: arguments come off the log with their
  // strings wrapped (see `toolArgs`), so a bare `typeof === 'string'` here found
  // nothing for any real call and this returned '' for every row in the app.
  const direct = argString(args)
  if (direct !== undefined) return firstLine(direct)
  if (args === null || typeof args !== 'object') return ''
  const record = args as Record<string, unknown>

  for (const key of IDENTIFYING_ARGS) {
    const value = argString(record[key])
    if (value !== undefined && value.trim().length > 0) {
      return firstLine(value)
    }
  }

  // Nothing recognised: fall back to the first string argument of any name, so
  // an unknown tool still says something rather than nothing.
  for (const candidate of Object.values(record)) {
    const value = argString(candidate)
    if (value !== undefined && value.trim().length > 0) {
      return firstLine(value)
    }
  }
  return ''
}

/**
 * How much output a result carries, for the hint on the collapsed row. Counting
 * lines (not bytes) is what tells someone whether expanding is worth it.
 */
export function summarizeResult(
  result: { text: string; isError?: boolean } | undefined,
): string {
  if (!result) return ''
  if (result.isError) return 'error'
  const text = result.text
  if (text.length === 0) return ''
  const lines = text.split('\n').filter((l) => l.length > 0).length
  if (lines === 0) return ''
  return lines === 1 ? '1 line' : `${lines} lines`
}

/**
 * The label for a collapsed run of calls: how many, and which tools they were.
 * Names are de-duplicated and capped, because "Bash, Bash, Bash, Bash" is the
 * exact repetition the grouping exists to absorb.
 */
export function summarizeToolNames(names: string[], max = 3): string {
  const distinct = [...new Set(names)]
  if (distinct.length <= max) return distinct.join(', ')
  return `${distinct.slice(0, max).join(', ')} +${distinct.length - max}`
}
